import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon, Search as SearchIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { snowflakeService } from '../services/snowflakeService';

const Manage: React.FC = () => {
  const navigate = useNavigate();
  const { userProfile, user, isAdmin } = useAuth();

  const [instructionName, setInstructionName] = useState<string>('TotalSERP_Analytics_Questions');
  const [instructionId, setInstructionId] = useState<number | null>(null);
  const presetInstructionNames = useMemo(
    () => [
      'TotalSERP_Analytics_Questions',
      'TotalSERP_Module1_Keyword_Analysis',
      'TotalSERP_Module3_Semantic_Analysis',
      'TotalSERP_Module4_Batch_Configuration',
    ],
    []
  );
  const [originalContent, setOriginalContent] = useState<string>('');
  const [editorContent, setEditorContent] = useState<string>('');
  const [contentType, setContentType] = useState<'text' | 'json'>('text');
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const hasUnsavedChanges = useMemo(() => editorContent !== originalContent, [editorContent, originalContent]);

  const loadInstruction = async (nameOverride?: string) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      const nameToLoad = nameOverride || instructionName;
      const res = await snowflakeService.getGuardrailInstruction(nameToLoad);
      if (!res.success) throw new Error(res.error || res.message);
      const data = res.data;
      // Capture id and metadata when available
      if (typeof data?.id === 'number') setInstructionId(data.id);
      else setInstructionId(null);

      const raw = data?.content;

      // Detect JSON vs text automatically
      let detectedType: 'json' | 'text' = 'text';
      let contentStr = '';
      if (raw !== undefined && raw !== null && typeof raw === 'object') {
        detectedType = 'json';
        contentStr = JSON.stringify(raw, null, 2);
      } else {
        const asString = String(raw ?? '');
        // Try to parse if it looks like JSON
        try {
          const parsed = JSON.parse(asString);
          if (parsed && typeof parsed === 'object') {
            detectedType = 'json';
            contentStr = JSON.stringify(parsed, null, 2);
          } else {
            detectedType = 'text';
            contentStr = asString;
          }
        } catch {
          detectedType = 'text';
          contentStr = asString;
        }
      }

      setContentType(detectedType);
      setOriginalContent(contentStr);
      setEditorContent(contentStr);
      if (nameOverride) setInstructionName(nameOverride);
    } catch (e: any) {
      setError(e.message || 'Failed to load instruction');
    } finally {
      setLoading(false);
    }
  };

  const saveInstruction = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // If JSON mode, validate JSON before saving
      let contentToSave = editorContent;
      if (contentType === 'json') {
        try {
          const parsed = JSON.parse(editorContent);
          // Save as pretty JSON string in Guardrail 'behavior'
          contentToSave = JSON.stringify(parsed);
        } catch (jsonErr: any) {
          setError('Invalid JSON. Please fix errors before saving.');
          setSaving(false);
          return;
        }
      }

      let res;
      if (instructionId !== null) {
        res = await snowflakeService.updateGuardrailInstructionById(instructionId, {
          title: instructionName,
          behavior: contentToSave,
          behaviorTypeId: 1,
          isPublic: false,
        });
      } else {
        // Fallback legacy update by name
        res = await snowflakeService.updateGuardrailInstruction(instructionName, contentToSave);
      }
      if (!res.success) throw new Error(res.error || res.message);
      setOriginalContent(editorContent);
      setSuccess('Instruction saved successfully');
    } catch (e: any) {
      setError(e.message || 'Failed to save instruction');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    loadInstruction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
            sx={{ mb: 2 }}
          >
            Back to Dashboard
          </Button>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
            Management Console
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Update Guardrail AI instructions centrally (admin only)
          </Typography>
        </Box>

        {!isAdmin && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            You do not have admin permissions to edit Guardrail instructions.
          </Alert>
        )}

        <Paper sx={{ p: 3 }}>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth disabled={loading || saving}>
                <InputLabel id="preset-instruction-label">Instruction</InputLabel>
                <Select
                  labelId="preset-instruction-label"
                  label="Instruction"
                  value={instructionName}
                  onChange={(e) => loadInstruction(String(e.target.value))}
                >
                  {presetInstructionNames.map((name) => (
                    <MenuItem key={name} value={name}>{name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md="auto" sx={{ ml: 'auto' }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={saveInstruction}
                disabled={!isAdmin || saving || loading || !hasUnsavedChanges}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </Grid>
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {contentType === 'json' ? (
                <Tabs value="json" onChange={() => {}} sx={{ mb: 2 }}>
                  <Tab value="json" label="JSON" />
                </Tabs>
              ) : (
                <Tabs value="text" onChange={() => {}} sx={{ mb: 2 }}>
                  <Tab value="text" label="Text" />
                </Tabs>
              )}

              <TextField
                fullWidth
                multiline
                minRows={18}
                value={editorContent}
                onChange={(e) => setEditorContent(e.target.value)}
                placeholder={contentType === 'json' ? '{\n  "key": "value"\n}' : 'Enter instruction text...'}
                InputProps={{ sx: { fontFamily: 'monospace' } }}
                disabled={!isAdmin}
              />
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default Manage;

